import React, { useState, useEffect } from "react";
import CustomButton from "../../../../common/CustomButton";
import { ROLE_MAPPING } from "../../../constants";
import { isUserMigratedToSscIam } from "../../../../common/ssciamAPI";
import {
  hasValidCognitoToken,
  loggerdInUserHasCognitoScope,
  loggedInUserHasSsciamScope,
} from "../../../../common/authHelper";
import { ErrorModal } from "./ErrorModal";
import "./UserTable.scss";

export const UserTable = ({
  users,
  onImpersonate,
  onResetPassword,
  onResetPhoneNumber,
  onRequestCognitoAuth,
  impersonatingUserId,
  portal2Host,
}) => {
  const [userMigrationStatus, setUserMigrationStatus] = useState({});
  const [loadingStatus, setLoadingStatus] = useState({});
  const [migrationCheckError, setMigrationCheckError] = useState(null);
  const [pendingCheckUser, setPendingCheckUser] = useState(null);

  useEffect(() => {
    if (pendingCheckUser && hasValidCognitoToken()) {
      const user = pendingCheckUser;
      setPendingCheckUser(null);
      checkMigrationStatus(user);
    }
  });

  const checkMigrationStatus = async (user) => {
    if (!hasValidCognitoToken()) {
      setPendingCheckUser(user);
      onRequestCognitoAuth();
      return;
    }
    setLoadingStatus((prev) => ({ ...prev, [user.id]: true }));

    try {
      const isMigrated = await isUserMigratedToSscIam(portal2Host, user.email);
      setUserMigrationStatus((prev) => ({ ...prev, [user.id]: isMigrated }));
    } catch (error) {
      console.error("Error checking migration status:", error);
      setMigrationCheckError(error.message || "Failed to check migration status");
    } finally {
      setLoadingStatus((prev) => ({ ...prev, [user.id]: false }));
    }
  };

  const handleResetAction = (user, isMigrated) => {
    return isMigrated ? onResetPhoneNumber(user) : onResetPassword(user);
  };

  const getStatusLabel = (userId) => {
    if (!(userId in userMigrationStatus)) {
      return { text: "Unchecked", className: "unchecked" };
    }
    return userMigrationStatus[userId]
      ? { text: "Migrated", className: "ssciam" }
      : { text: "Not Migrated", className: "cognito" };
  };

  const canResetUserPassword = loggerdInUserHasCognitoScope("idm_set_user_password");
  // there is something currently wrong with the ssciamToken. It sometimes does not send scopes correctly.
  const canResetPhoneNumber = loggedInUserHasSsciamScope("idm_set_user_password");

  const getUserRole = (user) => {
    if (
      !user.merchant_roles ||
      !user.merchant_roles[0] ||
      !user.merchant_roles[0].roles ||
      !user.merchant_roles[0].roles[0]
    ) {
      return "Unknown";
    }
    return ROLE_MAPPING[user.merchant_roles[0].roles[0]] || user.merchant_roles[0].roles[0];
  };

  return (
    <table className="new-table">
      <thead>
        <tr>
          <th style={{ textAlign: "left" }}>Id</th>
          <th style={{ textAlign: "left" }}>Email</th>
          <th style={{ textAlign: "left" }}>Role</th>
          <th style={{ textAlign: "center" }}>Actions</th>
          <th style={{ textAlign: "left" }}>Status</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        {users.map((user) => {
          const isMigrated = userMigrationStatus[user.id];
          const isLoading = loadingStatus[user.id];
          const hasCheckedStatus = user.id in userMigrationStatus;
          const statusLabel = getStatusLabel(user.id);
          const canDoAction = (isMigrated && canResetPhoneNumber) || (!isMigrated && canResetUserPassword);
          const isThisUserBeingImpersonated = impersonatingUserId === user.id;

          return (
            <tr key={user.id}>
              <td>
                <code className="short" title={user.id}>
                  {user.id}
                </code>
              </td>
              <td style={{ maxWidth: "250px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                <a href={"mailto:" + user.email}>{user.email}</a>
              </td>

              <td>{getUserRole(user)}</td>

              <td>
                {!hasCheckedStatus ? (
                  <CustomButton
                    onClick={() => checkMigrationStatus(user)}
                    loading={isLoading}
                    variant="text"
                    style={{ width: 155 }}
                  >
                    Check status
                  </CustomButton>
                ) : canDoAction ? (
                  <CustomButton
                    onClick={() => handleResetAction(user, isMigrated)}
                    variant="default"
                    style={{ width: 155 }}
                  >
                    {isMigrated ? "Reset Phone" : "Reset Password"}
                  </CustomButton>
                ) : (
                  <span>No permission</span>
                )}
              </td>
              <td>
                <span className={`status-label ${statusLabel.className}`}>{statusLabel.text}</span>
              </td>
              <td>
                <CustomButton
                  disabled={!!impersonatingUserId || !hasCheckedStatus}
                  loading={isThisUserBeingImpersonated}
                  onClick={() => onImpersonate(user.email, user.id, isMigrated)}
                  variant="default"
                  title={!hasCheckedStatus ? "Please check migration status first" : ""}
                >
                  Impersonate
                </CustomButton>
              </td>
            </tr>
          );
        })}
      </tbody>
      {migrationCheckError && (
        <ErrorModal
          title="Migration status check failed"
          message={migrationCheckError}
          onClose={() => setMigrationCheckError(null)}
        />
      )}
    </table>
  );
};
